import React, { useState } from 'react';
import './PollModal.css';

/**
 * Poll Modal Component
 * Simple modal dialog to create polls
 * 
 * Props:
 * - isOpen: whether modal is open
 * - onClose: callback to close modal
 * - onCreatePoll: callback when poll is created
 * - userName: current user name
 */
const PollModal = ({ isOpen, onClose, onCreatePoll, userName }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAddOption = () => {
    if (options.length < 5) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleCreatePoll = () => {
    setError('');

    // Validation
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    const validOptions = options.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      setError('Please enter at least 2 options');
      return;
    }

    setIsLoading(true);
    console.log('🗳️ PollModal: Creating poll with', { question, options: validOptions });

    // Call parent callback
    onCreatePoll({
      question: question.trim(),
      options: validOptions,
      createdBy: userName
    });

    // Reset and close
    setTimeout(() => {
      setQuestion('');
      setOptions(['', '']);
      setIsLoading(false);
      onClose();
    }, 500);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="poll-modal-backdrop" onClick={handleBackdropClick}>
      <div className="poll-modal">
        <div className="poll-modal-header">
          <h2>🗳️ Create Poll</h2>
          <button className="poll-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="poll-modal-content">
          {error && <div className="poll-modal-error">{error}</div>}

          {/* Question Input */}
          <div className="poll-modal-group">
            <label>Question</label>
            <input
              type="text"
              placeholder="Enter your poll question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={200}
              disabled={isLoading}
              className="poll-modal-input"
            />
            <span className="poll-char-count">{question.length}/200</span>
          </div>

          {/* Options Input */}
          <div className="poll-modal-group">
            <label>Options (2-5)</label>
            <div className="poll-options-list">
              {options.map((option, index) => (
                <div key={index} className="poll-option-input-group">
                  <span className="poll-option-num">{index + 1}.</span>
                  <input
                    type="text"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    maxLength={100}
                    disabled={isLoading}
                    className="poll-modal-input"
                  />
                  {options.length > 2 && (
                    <button
                      onClick={() => handleRemoveOption(index)}
                      disabled={isLoading}
                      className="poll-option-remove"
                      title="Remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {options.length < 5 && (
              <button
                onClick={handleAddOption}
                disabled={isLoading}
                className="poll-add-option-btn"
              >
                + Add Option
              </button>
            )}
          </div>
        </div>

        <div className="poll-modal-footer">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="poll-modal-btn cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleCreatePoll}
            disabled={isLoading || !question.trim()}
            className="poll-modal-btn create"
          >
            {isLoading ? '⏳ Creating...' : '🗳️ Create Poll'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PollModal;
